/**
 * Carries the map viewport across a route change.
 *
 * Map, AssetPreview and Telemetry each mount their own AssetMap, and the router
 * has no <keep-alive>, so navigating between them destroys one MapLibre instance
 * and builds another. A fresh instance starts at the coordinates given to its
 * constructor - historically [0, 0] at zoom 2, which with the globe projection of
 * the AWS style is the whole planet seen from orbit. That is why opening an asset
 * detail used to snap out to the globe and fly back in: the camera was not moving,
 * it was being rebuilt from scratch.
 *
 * The outgoing AssetMap parks its viewport here on unmount and the incoming one
 * picks it up, so the new map's first frame matches the last frame of the old one
 * and the only movement the user sees is the animation towards the asset.
 *
 * The value is consumed exactly once (see takeMapCamera): a view that mounts
 * without a pending handoff - a fresh login, a deep link, a reloaded tab - has no
 * previous viewport to continue from and correctly falls back to the globe.
 */
export type MapCamera = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

let pendingCamera: MapCamera | null = null;

/** Parks the viewport of a map that is about to be destroyed. */
export function storeMapCamera(camera: MapCamera): void {
  pendingCamera = camera;
}

/**
 * Returns the parked viewport and clears it, so a camera is never reused by a
 * second map. Returns null when there is nothing to continue from.
 */
export function takeMapCamera(): MapCamera | null {
  const camera = pendingCamera;
  pendingCamera = null;
  return camera;
}

/**
 * Drops any parked viewport. Called on logout: the next login must open on the
 * globe, not on whatever the previous user happened to be looking at.
 */
export function clearMapCamera(): void {
  pendingCamera = null;
}
